import { DryingGraphYAxis } from "@/utils/DryingGraphYAxis";
import { ComBoxSensorData } from "./ComBoxSensorData";

/**
 * Enumeration for different humidity units.
 */
export enum ComBoxHumidityUnit {
  RELATIVE_HUMIDITY = "RELATIVE_HUMIDITY",
  ABSOLUTE_HUMIDITY = "ABSOLUTE_HUMIDITY",
  MIXING_RATIO = "MIXING_RATIO",
}

export function getHumidityValue(
  humidityUnit: ComBoxHumidityUnit,
  comBoxSensorData: ComBoxSensorData
) {
  switch (humidityUnit) {
    case ComBoxHumidityUnit.RELATIVE_HUMIDITY:
      return comBoxSensorData.relativeHumidity;
    case ComBoxHumidityUnit.MIXING_RATIO:
      return comBoxSensorData.mixingRatio;
    case ComBoxHumidityUnit.ABSOLUTE_HUMIDITY:
      return comBoxSensorData.absoluteHumidity;
  }
}

export function toggleComBoxHumidityUnit(
  comBoxHumidityUnit: ComBoxHumidityUnit,
  preferAbsHumidity: boolean,
) {
  switch (comBoxHumidityUnit) {
    case ComBoxHumidityUnit.RELATIVE_HUMIDITY:
      if (preferAbsHumidity) {
        return ComBoxHumidityUnit.ABSOLUTE_HUMIDITY;
      }
      else {
        return ComBoxHumidityUnit.MIXING_RATIO;
      }

    default:
      return ComBoxHumidityUnit.RELATIVE_HUMIDITY;
  }
}

export function getComBoxHumidityUnitName(
  vue: Vue,
  comBoxHumidityUnit: ComBoxHumidityUnit
): string {
  switch (comBoxHumidityUnit) {
    case ComBoxHumidityUnit.RELATIVE_HUMIDITY:
      return vue.$t("comBox.humidityUnit.relativeHumidity").toString();
    case ComBoxHumidityUnit.MIXING_RATIO:
      return vue.$t("comBox.humidityUnit.mixingRatio").toString();
    case ComBoxHumidityUnit.ABSOLUTE_HUMIDITY:
      return vue.$t("comBox.humidityUnit.absoluteHumidity").toString();
  }
}

export function getComBoxHumidityUnitFormatRes(
  comBoxHumidityUnit: ComBoxHumidityUnit
): string {
  switch (comBoxHumidityUnit) {
    case ComBoxHumidityUnit.RELATIVE_HUMIDITY:
      return "comBox.humidityUnit.formatRelativeHumidity";
    case ComBoxHumidityUnit.MIXING_RATIO:
      return "comBox.humidityUnit.formatMixingRatio";
    case ComBoxHumidityUnit.ABSOLUTE_HUMIDITY:
      return "comBox.humidityUnit.formatAbsoluteHumidity";
  }
}

export function calculatePowerConsumption(
  power: number,
) {
  if (power === 65535) {
    return -255;
  }
  
  return power / 10;
}

export function calculateComBoxHumidityPd(
  temperature: number,
  relativeHumidity: number
) {
  return (
    Math.exp(23.462 - 3978.205 / (233.349 + temperature)) *
    (relativeHumidity / 100)
  );
}

export function calculateMixingRatio(pd: number) {
  return 0.622 * (pd / (101300 - pd)) * 1000;
}

export function calculateAbsoluteHumidity(temperature: number, pd: number) {
  return (pd / 461.51 / (273.15 + temperature)) * 1000;
}

export function getMatchingDryingGraphYAxis(
  comBoxHumidityUnit: ComBoxHumidityUnit
) {
  switch (comBoxHumidityUnit) {
    case ComBoxHumidityUnit.RELATIVE_HUMIDITY:
      return DryingGraphYAxis.COM_BOX_RELATIVE_HUMIDITY;
    case ComBoxHumidityUnit.MIXING_RATIO:
      return DryingGraphYAxis.COM_BOX_MIXING_RATIO;
    case ComBoxHumidityUnit.ABSOLUTE_HUMIDITY:
      return DryingGraphYAxis.COM_BOX_ABSOLUTE_HUMIDITY;
  }
}

export function getDefaultHumidityUnit() {
  return ComBoxHumidityUnit.RELATIVE_HUMIDITY;
}